import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  DeviceInfoProp,
  DeviceInfo,
  EligibleDevices,
  DeviceVariation,
  RepaymentProp,
  Repayment,
  SendDevice,
  CancelDeviceProp,
  CancelDevice,
  SendDeviceProp,
  DeviceHistory,
  DeviceVariationProp,
  DeviceRequestInfoProp,
  Device,
  EquityDeviceProp,
  SendDeviceRequest,
  DevicePaginationProp,
} from "styles/types/device";
import { AxiosError } from "axios";
import {
  calculateRepayment,
  cancelDeviceRequest,
  getDeviceImage,
  getDeviceInfo,
  getDevicePdf,
  getDeviceRequestHistory,
  getDeviceRequestInfo,
  getDeviceVariation,
  getEligibleDevices,
  getEquityContribution,
  sendDeviceRequest,
} from "store/api/device-finance";
import { Id } from "react-flags-select";
import { number } from "yup";

export const getAllEligibleDevices = createAsyncThunk(
  "device/getAllEligibleDevices",
  async (data: DevicePaginationProp, { rejectWithValue }) => {
    try {
      const response = await getEligibleDevices(data);
      return response?.data.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const deviceInfo = createAsyncThunk(
  "device/deviceInfo",
  async (values: DeviceInfoProp, { rejectWithValue }) => {
    try {
      const response = await getDeviceInfo(values);
      return JSON.parse(response?.data?.Data);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

// export const deviceInfo = createAsyncThunk(
//   "device/deviceInfo",
// async (values:DeviceInfoProp, thunkAPI:any) => {
//     try {
//       const data = await getDeviceInfo(
//         values.id, values.amount
//     );
//       return { wallet: data.data };
//     } catch (error:any) {
//       const message =
//         (error.response &&
//           error.response.data &&
//           error.response.data.message) ||
//         error.message ||
//         error.toString();
//       // thunkAPI.dispatch(setMessage(message));
//       return thunkAPI.rejectWithValue(message);
//     }
//   }
// );

export const deviceVariation = createAsyncThunk(
  "device/deviceVariation",
  async (values: DeviceVariationProp, { rejectWithValue }) => {
    try {
      const response = await getDeviceVariation(values);
      return response?.data?.Data.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const getRepaymentCalculation = createAsyncThunk(
  "device/getRepaymentCalculation",
  async (payload: RepaymentProp, { rejectWithValue }) => {
    try {
      const response = await calculateRepayment(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const deviceRequest = createAsyncThunk(
  "device/deviceRequest",
  async (values: SendDeviceProp, { rejectWithValue }) => {
    try {
      const response = await sendDeviceRequest(values);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const cancelDevice = createAsyncThunk(
  "device/cancelDevice",
  async (values: CancelDeviceProp, { rejectWithValue }) => {
    try {
      const response = await cancelDeviceRequest(values);
      return response?.data?.Message;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const deviceHistory = createAsyncThunk(
  "device/deviceHistory",
  async (payload:DevicePaginationProp, { rejectWithValue }) => {
    try {
      const response = await getDeviceRequestHistory(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const deviceRequestInfo = createAsyncThunk(
  "device/deviceRequestInfo",
  async (values: DeviceRequestInfoProp, { rejectWithValue }) => {
    try {
      const response = await getDeviceRequestInfo(values);
      return response?.data?.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const deviceEquityContribution = createAsyncThunk(
  "device/deviceEquityContribution",
  async (payload: EquityDeviceProp, { rejectWithValue }) => {
    try {
      const response = await getEquityContribution(payload);
      return response?.data?.Data.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const deviceImage = createAsyncThunk(
  "device/deviceImage",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getDeviceImage(id);
      return response?.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const devicePdf = createAsyncThunk(
  "device/devicePdf",
  async (pdfUrl: string, { rejectWithValue }) => {
    try {
      const response = await getDevicePdf(pdfUrl);
      return response?.data?.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

export const stepOneDeviceSlice = createAsyncThunk(
  "device/deviceStepOne",
  (stepOneData: any, thunkAPI: any) => {
    return { stepOneData: stepOneData };
  }
);

export const stepTwoDeviceSlice = createAsyncThunk(
  "device/deviceStepTwo",
  (stepTwoData: any, thunkAPI: any) => {
    return { stepTwoData: stepTwoData };
  }
);

export const stepThreeDeviceSlice = createAsyncThunk(
  "device/deviceStepThree",
  (stepThreeData: any, thunkAPI: any) => {
    return { stepThreeData: stepThreeData };
  }
);

export const selectedDeviceSlice = createAsyncThunk(
  "device/selectedDevice",
  (selectedDevice: any, thunkAPI: any) => {
    return { selectedDevice: selectedDevice };
  }
);

export const selectedBundleIDSlice = createAsyncThunk(
  "device/selectedBundleID",
  (selectedBundleID: any, thunkAPI: any) => {
    return { selectedBundleID: selectedBundleID };
  }
);

export const deviceEquityContributionSlice = createAsyncThunk(
  "device/deviceEquityAmount",
  (deviceEquityAmount: any, thunkAPI: any) => {
    return { deviceEquityAmount: deviceEquityAmount };
  }
);

export const stateAddressIDSlice = createAsyncThunk(
  "device/stateAddressID",
  (stateAddressID: any, thunkAPI: any) => {
    return { stateAddressID: stateAddressID };
  }
);



type DeviceFinanceState = {
  isLoading: boolean;
  error: string | null;
  //  allEligibleDevices: Device;
  allEligibleDevices: any;

  // deviceInfo: DeviceInfo;
  deviceInfo: any;
  deviceVariation: any;
  repayment: Repayment;
  sendDeviceRequest: SendDevice;
  cancelDevice: CancelDevice;
  // deviceHistory: DeviceHistory;
  deviceHistory: any;

  deviceRequestInfo: string;
  deviceImg: any;
  devicePdf: any;
  stepOneData: any;
  stepTwoData: any;
  stepThreeData:any;
  deviceEquityAmount:any;
  selectedDeviceData: any;
  selectedBundleID:any;
  stateAddressID:any;
  equityContribution: any;
};

const initialState: DeviceFinanceState = {
  isLoading: false,
  error: null,
  allEligibleDevices: [],
  // allEligibleDevices: {
  // Id: 0,
  // Name: "",
  // CategoryId: 0,
  // Model: "",
  // Price: 0,
  // Features: "",
  // BrandId: 0,
  // VendorId: 0,
  // Status: 0,
  // Image: "",
  // ImageByte: "",
  // IsFeatured: false,
  // IsPromo: false,
  // MonthlyRepayment: 0,
  // PhoneAmount: 0,
  // ActualAmountAdded: 0,
  // PhoneLoanAirtimeSum: 0,
  // LoanValue: 0,
  // Name1: "",
  // Name2: "",
  // PromoDiscount: "",
  // },
  deviceInfo: {
    // Bundles: "",
    // States: "",
    // Areas: "",
    // Networks: "",
    // Tenor: "",
    // Discount: "",
  },
  deviceVariation: {
    Colors: [],
    Rams: "",
    Roms: "",
    ProductId: "",
    Discount: "",
    Tenor: "",
  },
  repayment: {
    MonthlyRepayment: 0,
    LoanAmount: 0,
    MonthlyAirtimeCredit: 0,
    VendorPhoneNumber: "",
    IsAirtimeGreaterThanPMT: false,
  },
  sendDeviceRequest: {
    TransactionId: "",
    PdfUrl: "",
  },
  cancelDevice: {
    status: false,
    message: "",
    data: "",
  },
  deviceHistory: [],
  deviceRequestInfo: "",
  deviceImg: {},
  devicePdf: {},
  stepOneData: {},
  stepTwoData: {},
  stepThreeData:{},
  deviceEquityAmount:{},
  equityContribution: {},
  selectedDeviceData: {},
  stateAddressID:{},
  selectedBundleID:{}
};

const deviceFinanceSlice = createSlice({
  name: "device-finance",
  initialState: initialState,
  reducers: {
    resetSelectedBundleID: (state) => {
      state.selectedBundleID = 0; // Reset value to 0
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getAllEligibleDevices.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      getAllEligibleDevices.fulfilled,
      (state, action: PayloadAction<Device>) => {
        state.isLoading = false;
        state.error = null;
        state.allEligibleDevices = action.payload;
      }
    );

    builder.addCase(getAllEligibleDevices.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(deviceInfo.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceInfo.fulfilled,
      (state, action: PayloadAction<DeviceInfo>) => {
        state.isLoading = false;
        state.error = null;
        state.deviceInfo = action.payload;
      }
    );

    builder.addCase(deviceInfo.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(deviceVariation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceVariation.fulfilled,
      (state, action: PayloadAction<DeviceVariation>) => {
        state.isLoading = false;
        state.error = null;
        state.deviceVariation = action.payload;
      }
    );

    builder.addCase(deviceVariation.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(getRepaymentCalculation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      getRepaymentCalculation.fulfilled,
      (state, action: PayloadAction<Repayment>) => {
        state.isLoading = false;
        state.error = null;
        state.repayment = action.payload;
      }
    );

    builder.addCase(getRepaymentCalculation.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(deviceRequest.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceRequest.fulfilled,
      (state, action: PayloadAction<SendDevice>) => {
        state.isLoading = false;
        state.error = null;
        state.sendDeviceRequest = action.payload;
      }
    );

    builder.addCase(deviceRequest.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(cancelDevice.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      cancelDevice.fulfilled,
      (state, action: PayloadAction<CancelDevice>) => {
        state.isLoading = false;
        state.error = null;
        state.cancelDevice = action.payload;
      }
    );

    builder.addCase(cancelDevice.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(deviceHistory.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceHistory.fulfilled,
      (state, action: PayloadAction<DeviceHistory>) => {
        state.isLoading = false;
        state.error = null;
        state.deviceHistory = action.payload;
      }
    );

    builder.addCase(deviceHistory.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(deviceImage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceImage.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
        state.deviceImg = action.payload;
      }
    );

    builder.addCase(deviceImage.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(deviceRequestInfo.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceRequestInfo.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.error = null;
        state.deviceRequestInfo = action.payload;
      }
    );

    builder.addCase(deviceRequestInfo.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(devicePdf.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      devicePdf.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
        state.devicePdf = action.payload;
      }
    );

    builder.addCase(devicePdf.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });

    builder.addCase(selectedDeviceSlice.fulfilled, (state, action) => {
      state.selectedDeviceData = action.payload.selectedDevice;
    });
    builder.addCase(selectedDeviceSlice.rejected, (state) => {
      state.selectedDeviceData = null;
    });

    builder.addCase(selectedBundleIDSlice.fulfilled, (state, action) => {
      state.selectedBundleID = action.payload.selectedBundleID;
    });
    builder.addCase(selectedBundleIDSlice.rejected, (state) => {
      state.selectedBundleID = null;
    });

    builder.addCase(stepOneDeviceSlice.fulfilled, (state, action) => {
      state.stepOneData = action.payload.stepOneData;
    });
    builder.addCase(stepOneDeviceSlice.rejected, (state) => {
      state.stepOneData = null;
    });


    builder.addCase(stepTwoDeviceSlice.fulfilled, (state, action) => {
      state.stepTwoData = action.payload.stepTwoData;
    });
    builder.addCase(stepTwoDeviceSlice.rejected, (state) => {
      state.stepTwoData = null;
    });

    builder.addCase(stepThreeDeviceSlice.fulfilled, (state, action) => {
      state.stepThreeData = action.payload.stepThreeData;
    });
    builder.addCase(stepThreeDeviceSlice.rejected, (state) => {
      state.stepThreeData = null;
    });

    builder.addCase(deviceEquityContributionSlice.fulfilled, (state, action) => {
      state.deviceEquityAmount = action.payload.deviceEquityAmount;
    });
    builder.addCase(deviceEquityContributionSlice.rejected, (state) => {
      state.deviceEquityAmount = null;
    });

    builder.addCase(stateAddressIDSlice.fulfilled, (state, action) => {
      state.stateAddressID = action.payload.stateAddressID;
    });
    builder.addCase(stateAddressIDSlice.rejected, (state) => {
      state.stateAddressID = null;
    });

    builder.addCase(deviceEquityContribution.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      deviceEquityContribution.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
        state.equityContribution = action.payload;
      }
    );

    builder.addCase(deviceEquityContribution.rejected, (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || "Something went wrong";
      }
    });
  },
});
export const {  resetSelectedBundleID } = deviceFinanceSlice.actions;
export default deviceFinanceSlice.reducer;
